import { darken, lighten, alpha } from '@mui/material/styles';
import { brandColors, commonColors, legacyBrandColors, newSystemColors, systemColors } from './shared/palette';

export const legacyPalette = {
  mainaccent: '#18838C',
  secondaryaccent: '#e88126',
  darkaccent: '#20547b',
  darkaccentLight: '#4c7695',
  darkaccentDark: '#163a56',
  lightgray: '#fafafc',
  gray: '#eceff1',
  darkgray: '#939393',
  greyDivider: '#d8d4d4',
  favorable: '#55e0ec',
  neutral: '#f8e67e',
  unfavorable: '#f1b47c',
  red: '#c23628',
  blue: '#479ccf',
  green: '#219653',
  brandGrey: '#888f95',
  darkBlue: '#29567a',
  brandDarkBlue: '#255378',
  brandBlue: '#078296',
  brancLightBlue: '#21a6b3',
  brandOrange: '#eb7f00',
  divider: '#ccc',

  gray60: '#828282',
  gray20: '#e0e0e0',
  navy10: '#183f5b',
  navy100Opacity: '#e9eef2',

  sentiment: {
    positive: '#8de0a0',
    negative: '#feded0',
    neutral: '#fff6c3',
    unknown: '#efefef',
  },
};

/**
 * Uses built-in color-object types to handle contrast.
 *
 * TODO: replace with new palette and brand colors. Use `legacyPalette`
 * temporarily to fill out the same values until new branding is finalized.
 *
 * @see https://v4.mui.com/customization/palette/#providing-the-colors-directly
 */
export const palette = {
  mode: 'light' as const,
  contrastThreshold: 3,
  tonalOffset: 0.2,

  common: {
    ...commonColors,
    offWhite: legacyBrandColors.offWhite,
  },

  primary: {
    main: newSystemColors.blue.main,
    light: newSystemColors.blue.light,
    dark: newSystemColors.blue.dark,
    contrastText: commonColors.white,
  },

  secondary: {
    // orange
    main: newSystemColors.blue.main,
    // light: '',
    // dark: '',
    contrastText: commonColors.white,
  },

  tertiary: {
    // grey
    main: '#aaa',
    // light: '',
    // dark: '',
    contrastText: commonColors.black,
  },

  action: {
    active: '#939393',
    hover: 'rgba(0, 0, 0, .04)',
    hoverOpacity: 0.1,
    selected: 'rgba(0, 0, 0, .08)',
    selectedOpacity: 0.08,
    disabled: darken('#aaa', 0.25),
    disabledBackground: lighten('#aaa', 0.5),
    disabledOpacity: 0.38,
    focus: newSystemColors.blue.main,
    focusOpacity: 0.12,
    activatedOpacity: 0.12,
  },

  text: {
    title: newSystemColors.gray1, // very dark grey
    primary: newSystemColors.gray1, // dark grey
    secondary: '#888', // grey
    disabled: '#aaa', // light grey
    hint: '#e0e0e0', // light grey
  },

  background: {
    default: legacyBrandColors.offWhite, // off white
    app: legacyBrandColors.darkBlue, // dark blue
    header: commonColors.white, // white
    nav: legacyBrandColors.darkBlue, // dark blue
    paper: commonColors.white, // white
    unavailable: '#f0f0f0', // grey
  },

  grey: {
    50: '#fafafa',
    100: '#f5f5f5',
    200: '#eeeeee',
    300: '#e0e0e0',
    400: '#bdbdbd',
    500: '#9e9e9e',
    600: '#757575',
    700: '#616161',
    800: '#424242',
    900: '#212121',
    A100: '#d5d5d5',
    A200: '#aaaaaa',
    A400: '#303030',
    A700: '#616161',
    // These colors (main and dark) are needed to get the color="default" button behavior/styles (now defined as color="grey") working.
    // We need to do this since the <Button color="default" /> was removed in material-ui v5.
    main: '#e0e0e0',
    dark: '#bdbdbd',
  },

  error: {
    // red
    main: newSystemColors.error.main,
    light: '#fdeded',
    // dark: '',
    contrastText: darken(systemColors.error, 0.8),
  },

  success: {
    // green
    main: systemColors.success,
    light: lighten(systemColors.success, 0.9),
    // dark: '',
    contrastText: darken(systemColors.success, 0.8),
  },

  warning: {
    // yellow
    main: systemColors.warning,
    light: lighten(systemColors.warning, 0.6),
    // Standard MUI filled-warning orange, used for filled/emphasized warning states
    // (alerts, icons) since `main` above is overridden to a lighter yellow.
    dark: '#ED6C02',
    contrastText: darken(systemColors.warning, 0.8),
  },

  info: {
    // blue
    main: systemColors.info,
    light: lighten(systemColors.info, 0.75),
    // dark: '',
    contrastText: darken(systemColors.info, 0.8),
  },

  positive: {
    // green
    main: systemColors.positive,
    // light: '',
    // dark: '',
    contrastText: darken(systemColors.positive, 0.8),
  },

  negative: {
    // red
    main: systemColors.negative,
    // light: '',
    // dark: '',
    contrastText: darken(systemColors.negative, 0.8),
  },

  neutral: {
    // yellow
    main: systemColors.neutral,
    // light: '',
    // dark: '',
    contrastText: darken(systemColors.neutral, 0.8),
  },

  unknown: {
    // grey
    main: systemColors.unknown,
    // light: '',
    // dark: '',
    contrastText: darken(systemColors.unknown, 0.8),
  },

  'positive-sentiment': {
    main: '#0288D1',
    light: lighten('#0288D1', 0.2),
    dark: darken('#0288D1', 0.2),
    contrastText: '#fff',
  },
  'negative-sentiment': {
    main: '#EF5350',
    light: lighten('#EF5350', 0.2),
    dark: darken('#EF5350', 0.2),
    contrastText: '#fff',
  },
  'neutral-sentiment': {
    main: alpha('#000000', 0.08),
    light: alpha('#000000', 0.06),
    dark: alpha('#000000', 0.2),
    contrastText: alpha('#000000', 0.87),
  },
  'unknown-sentiment': {
    main: alpha('#000000', 0.6),
    light: alpha('#000000', 0.4),
    dark: alpha('#000000', 0.7),
    contrastText: '#fff',
  },

  divider: newSystemColors.gray4,

  brandBlue: brandColors.blue,
  brandAqua: brandColors.aqua,
  brandLightAqua: brandColors.lightAqua,
  brandPurple: brandColors.purple,
  brandLightPurple: brandColors.lightPurple,
  brandNeonGreen: brandColors.neonGreen,
  brandOffWhite: brandColors.offWhite,

  white: newSystemColors.white,
  gray6: newSystemColors.gray6,
  gray5: newSystemColors.gray5,
  gray4: newSystemColors.gray4,
  gray3: newSystemColors.gray3,
  gray2: newSystemColors.gray2,
  gray1: newSystemColors.gray1,
  navGray: newSystemColors.navGray,
};
